'use client';

import { CategoryIcon } from '@/components/CategoryIcon';
import { useAppSettings } from '@/hooks/useAppSettings';
import { formatMoney } from '@/lib/money';
import { Category } from '@/lib/types';

export default function CategoryBudgetCard({ category, spent }: { category: Category; spent: number }) {
  const { settings } = useAppSettings();
  const money = (value: number) => formatMoney(value, settings.currency, 0);
  const hasBudget = typeof category.monthlyBudget === 'number' && category.monthlyBudget >= 0;
  const ratio = hasBudget && category.monthlyBudget! > 0 ? Math.min(spent / category.monthlyBudget!, 1) : 0;
  const remaining = hasBudget ? category.monthlyBudget! - spent : 0;
  const warning = hasBudget ? remaining < 0 : false;

  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900 p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <CategoryIcon category={category} className="h-10 w-10" />
          <div className="min-w-0">
            <p className="truncate font-semibold text-slate-100">{category.name}</p>
            <p className="mt-1 text-sm text-slate-400">
              {hasBudget ? `Budget ${money(category.monthlyBudget ?? 0)}` : 'Pas de budget défini'}
            </p>
          </div>
        </div>
        <div className={`rounded-full px-3 py-1 text-xs font-semibold ${warning ? 'bg-rose-500/15 text-rose-200' : 'bg-emerald-500/15 text-emerald-200'}`}>
          {warning ? 'Dépassé' : hasBudget ? 'Dans le budget' : 'Libre'}
        </div>
      </div>
      {hasBudget ? (
        <>
          <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-800">
            <div className="h-full rounded-full" style={{ width: `${ratio * 100}%`, backgroundColor: warning ? '#f43f5e' : category.color ?? '#3b9d55' }} />
          </div>
          <div className="mt-3 flex items-center justify-between gap-3 text-sm text-slate-400">
            <span>{money(spent)} dépensés</span>
            <span className={warning ? 'font-semibold text-rose-300' : 'font-semibold text-brand-300'}>{money(remaining)} restants</span>
          </div>
        </>
      ) : (
        <div className="mt-4 rounded-2xl bg-slate-950 p-3 text-sm text-slate-400">Aucune limite pour cette catégorie.</div>
      )}
    </div>
  );
}
